import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, ShieldCheck, UserCog, Calculator, Tractor, Eye } from "lucide-react";

const ROLES = ["admin", "gérant", "comptable", "ouvrier", "visiteur"];
const ROLE_ICONS = { admin: ShieldCheck, "gérant": UserCog, comptable: Calculator, ouvrier: Tractor, visiteur: Eye };
const ROLE_COLORS = {
  admin: "bg-primary text-primary-foreground",
  "gérant": "bg-[#4A7C59] text-white",
  comptable: "bg-[#3A86FF] text-white",
  ouvrier: "bg-[#DDA15E] text-[#1A1A1A]",
  visiteur: "bg-[#8D99AE] text-white",
};

export default function Utilisateurs() {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [newU, setNewU] = useState({ name: "", email: "", password: "", role: "ouvrier" });

  async function load() {
    const r = await api.get("/users");
    setUsers(r.data);
  }
  useEffect(() => { load().catch(() => toast.error(t("error"))); }, []);

  async function addUser() {
    if (!newU.name || !newU.email || !newU.password) return toast.error("Nom, email & mot de passe requis");
    try {
      await api.post("/users", newU);
      setOpen(false); setNewU({ name: "", email: "", password: "", role: "ouvrier" });
      toast.success(t("added")); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }
  async function changeRole(id, role) {
    try {
      await api.put(`/users/${id}/role`, { role });
      toast.success(t("updated")); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">{t("role_admin")}</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{t("users")}</h1>
          <p className="text-muted-foreground mt-1">{users.length} {t("users").toLowerCase()}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-user-btn"><Plus className="w-4 h-4 mr-2" />{t("add_user")}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("add_user")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t("name")}</Label><Input data-testid="user-name" value={newU.name} onChange={(e) => setNewU({ ...newU, name: e.target.value })} /></div>
              <div><Label>{t("email")}</Label><Input type="email" data-testid="user-email-input" value={newU.email} onChange={(e) => setNewU({ ...newU, email: e.target.value })} /></div>
              <div><Label>{t("password")}</Label><Input type="password" data-testid="user-password" value={newU.password} onChange={(e) => setNewU({ ...newU, password: e.target.value })} /></div>
              <div>
                <Label>{t("role")}</Label>
                <Select value={newU.role} onValueChange={(v) => setNewU({ ...newU, role: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{t(`role_${r === "gérant" ? "gerant" : r}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={addUser} data-testid="user-save">{t("save")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-border">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left border-b border-border bg-secondary/50">
              <tr>
                <th className="p-3">{t("name")}</th>
                <th>{t("email")}</th>
                <th>{t("role")}</th>
                <th>{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const Icon = ROLE_ICONS[u.role] || UserCog;
                return (
                  <tr key={u.id} className="border-b border-border/60 last:border-0" data-testid={`user-row-${u.id}`}>
                    <td className="p-3 font-medium flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${ROLE_COLORS[u.role] || "bg-secondary"}`}>
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                      {u.name}
                    </td>
                    <td className="text-muted-foreground">{u.email}</td>
                    <td><Badge variant="secondary">{t(`role_${u.role === "gérant" ? "gerant" : u.role}`)}</Badge></td>
                    <td>
                      <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                        <SelectTrigger className="h-8 w-36" data-testid={`user-role-select-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{t(`role_${r === "gérant" ? "gerant" : r}`)}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {ROLES.map((r) => {
          const Icon = ROLE_ICONS[r];
          return (
            <Card key={r} className="border-border">
              <CardContent className="p-4">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${ROLE_COLORS[r]}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="font-semibold">{t(`role_${r === "gérant" ? "gerant" : r}`)}</div>
                <div className="text-xs text-muted-foreground mt-1">{t(`role_desc_${r === "gérant" ? "gerant" : r}`)}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
